import { kArtifactUpdateVersionPath } from '@/constants/constants-api-paths'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { getAuth } from 'firebase/auth'
import { updateArtifactVersion } from '@/artifacts/fetch/put-artifact-version'

/**
 * Emergency save function for beforeunload scenarios
 * Uses multiple strategies to ensure data is saved during page unload
 */
export async function saveArtifactBeforeUnload(artifactVersionId: string, content: string): Promise<boolean> {
  try {
    // First try: Use the existing updateArtifactVersion function
    // This handles all the proper error handling, auth, etc.
    await updateArtifactVersion({
      artifactVersionId,
      content,
    })
    return true
  } catch {
    // If the normal method fails, try emergency beacon/keepalive methods
    return await emergencySave(artifactVersionId, content)
  }
}

/**
 * Emergency save using sendBeacon and fetch keepalive for page unload scenarios
 */
async function emergencySave(artifactVersionId: string, content: string): Promise<boolean> {
  try {
    // Get auth token
    const token = await getAuth().currentUser?.getIdToken()
    if (!token) {
      throw new Error('No auth token available')
    }

    // Build the API endpoint and payload
    const apiUrl = kPaxtonAppApiRESTBaseUrl() + kArtifactUpdateVersionPath.replace('{artifactVersionId}', artifactVersionId)
    const payload = JSON.stringify({
      content,
      parentVersionId: artifactVersionId,
    })

    // Option A: Try sendBeacon first (most reliable during page unload)
    const beaconSuccess =
      navigator.sendBeacon &&
      navigator.sendBeacon(
        apiUrl,
        new Blob([payload], {
          type: 'application/json',
        })
      )

    if (beaconSuccess) {
      return true
    }

    // Option B: Fallback to fetch with keepalive
    const headers = new Headers({
      'Content-Type': 'application/json; charset=UTF-8',
      Authorization: `Bearer ${token}`,
    })

    const response = await fetch(apiUrl, {
      method: 'PUT',
      keepalive: true,
      body: payload,
      headers: headers,
    })

    return response.ok
  } catch (error) {
    console.error('Emergency save failed:', error)
    return false
  }
}
